import React from 'react'
import { useSelector } from 'react-redux'
import { FormikErrors, FormikHandlers } from 'formik'
import { TextFieldTypes } from 'views/components/atoms/TextField/text-field.types'
import Button from 'components/atoms/Button/Button'
import InfoBox from 'components/atoms/InfoBox/InfoBox'
import Label from 'components/atoms/Label/Label'
import TextField from 'components/atoms/TextField/TextField'
import TextInput from 'components/atoms/TextField/TextInput'
import FormLoader from 'components/molecules/FormLoader/FormLoader'
import Column from 'components/molecules/Grid/Column'
import Grid from 'components/molecules/Grid/Grid'
import { apiStateSelectors } from 'modules/api/apiState'
import { propertyApiEvents } from 'modules/api/property'
import styles from './PropertyForm.module.scss'
import { isEmpty } from 'ramda'
import { IPropertyFormValues } from './types'

interface IPropertyPreviewProps {
  initialValues: IPropertyFormValues
  values: IPropertyFormValues
  errors: FormikErrors<IPropertyFormValues>
  dirty: boolean
  handleChange: FormikHandlers['handleChange']
  handleBlur: FormikHandlers['handleBlur']
  clearSelection: () => void
  handleConfirmation: (e: React.FormEvent<HTMLFormElement>) => void
  isDraftPortfolio: boolean
  selectedPropertyType: string
}

interface IButtonContainerProps {
  clearSelection: () => void
  isSubmitting: boolean
  children: React.ReactNode
}

/**
 * Module-level on purpose. A component built during render gets a new element type every time, so
 * React remounts the footer — and the submit button with it — on every re-render. Blurring a field
 * re-renders synchronously between mousedown and mouseup, which detaches the button mid-click and
 * swallows the submit entirely. Vary this through `ButtonContainerProps`, never by rebuilding it.
 */
const ButtonContainer = ({ clearSelection, isSubmitting, children }: IButtonContainerProps): React.ReactElement => (
  <div className={styles.footer}>
    {!isSubmitting && (
      <Button size="sm" secondary onClick={clearSelection}>
        Change property
      </Button>
    )}
    {children}
  </div>
)
ButtonContainer.displayName = 'ButtonContainer'

const PropertyPreview = ({
  initialValues,
  handleChange,
  handleBlur,
  values,
  errors,
  dirty,
  clearSelection,
  handleConfirmation,
  isDraftPortfolio,
  selectedPropertyType,
}: IPropertyPreviewProps): React.ReactElement => {
  /**
   * @todo add portfolio creation here as well. Not available in apiState because of forkEpic
   * @link https://github.com/CastleOne/robin-client/blob/557e06304c5a211ebc7688757111b48bd7ae2505/src/modules/ui/ChatBot/portfolioEpics.js#L37
   */
  const isSubmitting = useSelector(state =>
    apiStateSelectors.isLoading(state)([propertyApiEvents.createProperty_request]),
  )
  const formLoaderState = isSubmitting ? 'submitting' : undefined

  const shouldShowSectionalSchemeDetails = !isEmpty(values.schemeNumber) && !isEmpty(values.schemeName)
  const isGooglePlaceSelected = selectedPropertyType === 'Google street address'

  return (
    <div className={styles.root}>
      <FormLoader
        onSubmit={handleConfirmation}
        state={formLoaderState}
        buttonProps={{
          children: !isDraftPortfolio ? 'Save' : 'Next',
          disabled: !isEmpty(errors) || (!isDraftPortfolio && !dirty),
        }}
        ButtonContainer={ButtonContainer}
        ButtonContainerProps={{ clearSelection, isSubmitting }}
      >
        {selectedPropertyType && (
          <div className={styles.header}>
            <Label
              circular
              className={`${styles['property-type']} ${
                styles[`property-type--${selectedPropertyType.toLowerCase().replace(/\s/g, '-')}`]
              }`}
            >
              {selectedPropertyType}
            </Label>
          </div>
        )}
        <div className={styles.body}>
          <Grid>
            <Column xs={12} md={12} flexOptions="padding-r-0">
              <TextField
                inputComponent={
                  <TextInput
                    type={TextFieldTypes.text}
                    name="propertyDescription"
                    placeholder="e.g. Granny flat"
                    value={values.propertyDescription}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    disabled={false}
                  />
                }
                error={errors.propertyDescription}
                label="Lease label"
                labelHint="Used internally to distinguish multiple leases at the same property. Not visible to tenants or owners. e.g. Granny flat, Main house, Room 1"
              />
            </Column>
            {shouldShowSectionalSchemeDetails || isGooglePlaceSelected ? (
              <>
                <Column xs={12} md={4} flexOptions="padding-r-1">
                  <TextField
                    inputComponent={
                      <TextInput
                        type={TextFieldTypes.text}
                        name="unitNumber"
                        placeholder=""
                        value={values.unitNumber}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        disabled={false}
                      />
                    }
                    label="Unit number"
                  />
                </Column>
                <Column xs={12} md={8} flexOptions="padding-r-0">
                  <TextField
                    inputComponent={
                      <TextInput
                        type={TextFieldTypes.text}
                        name="buildingName"
                        placeholder=""
                        value={values.buildingName}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        disabled={false}
                      />
                    }
                    label="Building/complex name"
                  />
                </Column>
              </>
            ) : null}
          </Grid>

          {/* Read Only Information */}
          <InfoBox type="locked" className={styles['info-box']}>
            <Grid>
              <Column className={styles['read-only-column']} xs={12} md={12} flexOptions="padding-r-0">
                <h3>Property details</h3>
              </Column>
              {values.schemeNumber && (
                <Column className={styles['read-only-column']} xs={12} md={6} flexOptions="padding-r-0">
                  <div className={styles['read-only-heading']}>Sectional scheme number</div>
                  <span>{values.schemeNumber}</span>
                </Column>
              )}
              {values.schemeName && (
                <Column className={styles['read-only-column']} xs={12} md={6} flexOptions="padding-r-0">
                  <div className={styles['read-only-heading']}>Sectional scheme name</div>
                  <span>{values.schemeName}</span>
                </Column>
              )}
              <Column className={styles['read-only-column']} xs={12} md={12} flexOptions="padding-r-0">
                <div className={styles['read-only-heading']}>Street address</div>
                <span>{values.streetAddress}</span>
              </Column>
              <Column className={styles['read-only-column']} xs={12} md={6} flexOptions="padding-r-0">
                <div className={styles['read-only-heading']}>City/municipality</div>
                <span>{values.city}</span>
              </Column>
              <Column className={styles['read-only-column']} xs={12} md={6} flexOptions="padding-r-0">
                <div className={styles['read-only-heading']}>Province</div>
                <span>{values.province}</span>
              </Column>
              <Column className={styles['read-only-column']} xs={12} md={6} flexOptions="padding-r-0">
                <div className={styles['read-only-heading']}>Postal code</div>
                <span>{values.postalCode}</span>
              </Column>
              <Column className={styles['read-only-column']} xs={12} md={6} flexOptions="padding-r-0">
                <div className={styles['read-only-heading']}>Country</div>
                <span>{values.country}</span>
              </Column>
            </Grid>
          </InfoBox>
        </div>
      </FormLoader>
    </div>
  )
}

export default PropertyPreview
